import { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Cpu, CheckCircle2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { todayIso } from '@/components/quotes/ServiceDialogs';
import { showApiError } from '@/lib/apiErrors';

const API_URL = process.env.REACT_APP_API_URL;
const LOCALES = { tr: 'tr-TR', en: 'en-US', de: 'de-DE', fr: 'fr-FR', es: 'es-ES' };

/**
 * On an accepted quote: record its products as installed at the customer
 * (with install date and warranty) so they show up on the service page.
 * Piece-counted products are pre-ticked; cable, paint, chemicals and other
 * consumables are not — the user decides.
 */
export function QuoteDevicesCard({ quote }) {
  const { t, i18n } = useTranslation('quotes');
  const locale = LOCALES[(i18n.language || 'tr').split('-')[0]] || 'tr-TR';
  const fmt = (n) => new Intl.NumberFormat(locale, { maximumFractionDigits: 3 }).format(Number(n) || 0);
  const [count, setCount] = useState(null);
  const [lines, setLines] = useState(null);
  const [checked, setChecked] = useState({});
  const [open, setOpen] = useState(false);
  const [warranty, setWarranty] = useState('24');
  const [installed, setInstalled] = useState(todayIso());
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/quote/service/devices/by-quote/${quote.id}`);
      setCount(data.count);
      if (!data.count && quote.customer_id) {
        const { data: preview } = await axios.get(
          `${API_URL}/api/quote/service/devices/from-quote/${quote.id}/preview`,
          { params: { package_key: quote.accepted_package_key || undefined } },
        );
        const rows = preview.lines || [];
        setLines(rows);
        setChecked(Object.fromEntries(rows.map((l) => [l.key, !!l.suggested])));
      }
    } catch {
      setCount(-1);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quote.id]);

  const customerId = quote.customer_id;
  if (count === null || count < 0 || !customerId) return null;
  // nothing in this quote that could be a device (e.g. a pure service quote)
  if (count === 0 && (!lines || lines.length === 0)) return null;

  const selectedKeys = Object.keys(checked).filter((k) => checked[k]);

  const save = async () => {
    if (!selectedKeys.length) return;
    setSaving(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/quote/service/devices/from-quote/${quote.id}`, {
        warranty_months: parseInt(warranty, 10) || 0,
        installed_at: installed || undefined,
        package_key: quote.accepted_package_key || undefined,
        keys: selectedKeys,
      });
      toast.success(t('service.fromQuote.doneToast', { count: data.created }));
      setOpen(false);
      load();
    } catch (err) {
      showApiError(err, t);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mb-6 flex flex-wrap items-center gap-3 rounded-2xl border border-[#E5E5E5] bg-white p-4" data-testid="quote-devices-card">
      <Cpu className="h-5 w-5 text-[#2563EB]" />
      <div className="min-w-0 flex-1">
        <div className="font-semibold">{t('service.fromQuote.title')}</div>
        <div className="text-sm text-[#595959]">
          {count > 0 ? t('service.fromQuote.done', { count }) : t('service.fromQuote.help')}
        </div>
      </div>
      {count > 0 ? (
        <Link to={`/quote-service?customer=${customerId}`} className="flex items-center gap-1.5 text-sm font-medium text-[#2563EB]">
          <CheckCircle2 className="h-4 w-4 text-emerald-600" /> {t('service.fromQuote.open')}
        </Link>
      ) : (
        <Button size="sm" onClick={() => setOpen(true)} className="bg-[#2563EB] hover:bg-[#1E4ED8]" data-testid="quote-devices-add">
          {t('service.fromQuote.button')}
        </Button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('service.fromQuote.title')}</DialogTitle>
            <DialogDescription>{t('service.fromQuote.dialogHelp')}</DialogDescription>
          </DialogHeader>
          <ul className="max-h-[40vh] divide-y divide-[#F1F5F9] overflow-y-auto rounded-xl border border-[#E5E5E5]">
            {(lines || []).map((l) => (
              <li key={l.key}>
                <label className="flex cursor-pointer items-center gap-3 px-3 py-2.5">
                  <Checkbox
                    checked={!!checked[l.key]}
                    onCheckedChange={(v) => setChecked((prev) => ({ ...prev, [l.key]: !!v }))}
                    data-testid={`quote-devices-line-${l.key}`}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-[#1A1A2E]">{l.name}</span>
                    <span className="block truncate text-xs text-[#94A3B8]">
                      {fmt(l.quantity)} {l.unit}{l.category ? ` · ${l.category}` : ''}
                    </span>
                  </span>
                </label>
              </li>
            ))}
          </ul>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>{t('service.device.installedAt')}</Label>
              <Input type="date" value={installed} onChange={(e) => setInstalled(e.target.value)} />
            </div>
            <div>
              <Label>{t('service.fromQuote.warranty')}</Label>
              <select value={warranty} onChange={(e) => setWarranty(e.target.value)}
                className="h-10 w-full rounded-md border border-[#E5E5E5] bg-white px-3 text-sm">
                {[0, 12, 24, 36].map((m) => (
                  <option key={m} value={m}>{m ? t('service.fromQuote.months', { count: m }) : t('service.fromQuote.noWarranty')}</option>
                ))}
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t('common.cancel')}</Button>
            <Button onClick={save} disabled={saving || !selectedKeys.length} className="bg-[#2563EB] hover:bg-[#1E4ED8]" data-testid="quote-devices-save">
              {t('service.fromQuote.saveSelected', { count: selectedKeys.length })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
